"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Link as RouterLink } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const articles = [{
  slug: "soc-grc-security-operating-model",
  title: "Improving GRC Efficiency Without Overloading the SOC Team",
  category: "Cybersecurity Governance",
  date: "June 2026",
  readingTime: "7",
  image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1400&q=80",
  summary: "How SOC operational evidence can strengthen governance, risk management, control validation, and audit readiness."
}, {
  slug: "ai-data-centers-infrastructure-security-governance",
  title: "AI Data Centers",
  category: "AI Infrastructure",
  date: "June 2026",
  readingTime: "8",
  image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=80",
  summary: "AI data centers require a new operating model across compute density, power, cooling, network fabric, cybersecurity, and governance."
}];
function RelatedArticlesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-64 rounded-lg bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-muted-foreground py-8 font-sans">
        Failed to load expert insights.
      </div>;
  }
  return <div className="space-y-12">
      {}
      {posts && posts.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="block h-full group outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg" data-index={i}>
              <Card className="h-full flex flex-col border-border/50 shadow-sm hover:shadow-lg transition-all duration-300 bg-card overflow-hidden hover:border-primary/50">
                {post.featuredImage && <div className="w-full h-48 overflow-hidden bg-muted">
                    <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </div>}
                
                <CardContent className="p-6 flex flex-col flex-grow">
                  <div className="mb-4">
                    <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none px-3 py-1 rounded-md text-xs font-bold tracking-wider uppercase font-mono">
                      {post.categories && post.categories.length > 0 ? post.categories[0].name : "Insight"}
                    </Badge>
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground font-default mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-200" dangerouslySetInnerHTML={{
              __html: post.title
            }} />
                  
                  {post.excerpt && <div className="text-muted-foreground font-default text-sm leading-relaxed line-clamp-3 flex-grow" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                </CardContent>
              </Card>
            </Link>)}
        </div>}
      {}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((article, i) => <RouterLink key={article.slug} to={`/InsightsPage?slug=${article.slug}`} className="block h-full group outline-none focus-visible:ring-2 focus-visible:ring-[#13B8C8] rounded-lg" data-index={i}>
            <Card className="h-full flex flex-col border border-white/10 shadow-md hover:shadow-xl transition-all duration-300 bg-[#142033] overflow-hidden hover:border-[#13B8C8]/50">
              <div className="w-full h-48 overflow-hidden bg-[#0B1220]">
                <img src={article.image} data-wvc-srcset={`${article.image}-wvc-srcset`} data-wvc-sizes={`${article.image}-wvc-sizes`} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              
              <CardContent className="p-6 flex flex-col flex-grow">
                <div className="mb-4">
                  <Badge className="bg-[#13B8C8]/10 text-[#13B8C8] border-none px-3 py-1 rounded-md text-xs font-bold tracking-wider uppercase font-mono">
                    {article.category}
                  </Badge>
                </div>
                
                <h3 className="text-xl font-bold text-[#F8FAFC] font-default mb-3 line-clamp-2 group-hover:text-[#13B8C8] transition-colors duration-200">
                  {article.title}
                </h3>
                
                <p className="text-[#CBD5E1] font-default text-sm leading-relaxed line-clamp-3 flex-grow mb-4">
                  {article.summary}
                </p>

                <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
                  <span className="text-xs text-[#CBD5E1]/60 font-mono">
                    {article.date} • {article.readingTime} min read
                  </span>
                  <span className="text-[#13B8C8] text-sm font-bold group-hover:translate-x-1 transition-transform duration-200">
                    Read Article →
                  </span>
                </div>
              </CardContent>
            </Card>
          </RouterLink>)}
      </div>
    </div>;
}
export default function InsightAreas() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="257670" id="insights-articles" className="py-16 md:py-24 bg-background text-foreground scroll-mt-24">
      <div className="page-container">
        <div className="mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground font-default tracking-tight">Featured Research &amp; Insights</h2>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <RelatedArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}